import * as React from 'react'
import type { GridApi } from '@mui/x-data-grid-premium'
import type ExcelJS from 'exceljs'

export type RegisteredSheet =
  | {
      kind: 'grid'
      id: string
      sheetName: string
      apiRef: React.MutableRefObject<GridApi | null>
      order?: number
      rowSpanFields?: string[]
      rowSpanGroupByField?: string
    }
  | {
      kind: 'static'
      id: string
      sheetName: string
      order?: number
      render: (ws: ExcelJS.Worksheet) => void
    }

type RegistryContextValue = {
  register: (grid: RegisteredSheet) => void
  unregister: (id: string) => void
  list: () => RegisteredSheet[]
}

const ExportRegistryContext = React.createContext<RegistryContextValue | null>(null)

export function useExportRegistry() {
  const ctx = React.useContext(ExportRegistryContext)
  if (!ctx) throw new Error('useExportRegistry must be used within ExportRegistryProvider')
  return ctx
}

export function useRegisterGridForExport(args: {
  enabled?: boolean
  id?: string
  sheetName?: string
  apiRef: React.MutableRefObject<GridApi | null>
  order?: number
  rowSpanFields?: string[]
  rowSpanGroupByField?: string
}) {
  const registry = useExportRegistry()
  const { enabled = true, id, sheetName, apiRef, order, rowSpanFields, rowSpanGroupByField } = args

  React.useEffect(() => {
    if (!enabled) return
    if (!registry) return
    if (!id || !sheetName) return

    registry.register({ kind: 'grid', id, sheetName, apiRef, order, rowSpanFields, rowSpanGroupByField })
    return () => registry.unregister(id)
  }, [enabled, id, sheetName, apiRef, order, registry, rowSpanFields, rowSpanGroupByField])
}

export function useRegisterStaticSheetForExport(args: {
  enabled?: boolean
  id?: string
  sheetName?: string
  order?: number
  render?: (ws: ExcelJS.Worksheet) => void
}) {
  const registry = useExportRegistry()
  const { enabled = true, id, sheetName, order, render } = args

  React.useEffect(() => {
    if (!enabled) return
    if (!registry) return
    if (!id || !sheetName || !render) return

    registry.register({ kind: 'static', id, sheetName, order, render })
    return () => registry.unregister(id)
  }, [enabled, id, sheetName, order, render, registry])
}

export default function ExportRegistryProvider({ children }: { children: React.ReactNode }) {
  const gridsRef = React.useRef(new Map<string, RegisteredSheet>())

  const register = React.useCallback((grid: RegisteredSheet) => {
    gridsRef.current.set(grid.id, grid)
  }, [])

  const unregister = React.useCallback((id: string) => {
    gridsRef.current.delete(id)
  }, [])

  const list = React.useCallback(() => {
    return Array.from(gridsRef.current.values()).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  }, [])

  const value = React.useMemo(() => ({ register, unregister, list }), [register, unregister, list])

  return <ExportRegistryContext.Provider value={value}>{children}</ExportRegistryContext.Provider>
}
